// @flow
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Heading, Stack, Spinner, DisplayHtmlContent } from 'sarsaparilla';
import get from 'lodash/get';
import toString from 'lodash/toString';

import PermitMap from 'ui-permit/src/shared/components/PermitMap';
import { IS_LAND_PERMIT } from 'ui-land-permit/src/utils/configUtils';
import { LOWERDESCHUTES_PERMIT_ID } from 'ui-lowerdeschutes/src/constants';
import { BOUNDARY_WATERS_ID, GRAND_TETON_ID } from '../../src/constants';

import type { PermitType, DivisionType } from '../types';

const MAP_STYLE = {
    position: 'relative',
    backgroundColor: '#e3e3e3',
    backgroundRepeat: 'none',
    backgroundSize: 'cover',
    backgroundPosition: '50%',
    height: '35rem',
}

const DivisionDescription = ({ division }: { division: DivisionType }) => (
    <DisplayHtmlContent html={get(division, 'description', '')} />
);

const DivisionNeedToKnow = ({ division }: { division: DivisionType }) => division?.description_map?.need_to_know ? (
    <Stack>
        <Heading headingLevel={2} appearance="h5">
            Need to know
        </Heading>
        <DisplayHtmlContent html={division?.description_map?.need_to_know} />
    </Stack>
) : null;

const filterFeatures = (geoDataGeometry, divisionId) => {
    const features = geoDataGeometry?.features;
    if(!features?.length) {
        return undefined;
    }

    const divisionFeature = features.find((feature) => {
        const featureId = feature?.properties?.id ? toString(feature?.properties?.id) : null;
        return !!featureId && divisionId === featureId;
    })

    if(divisionFeature) {
        return {
            ...geoDataGeometry,
            features: [divisionFeature],
        };
    }
    return undefined;
}

const DivisionMap = ({ division }: { division: DivisionType }) => {
    const [geoData, setGeoData] = useState({});
    const [points, setPoints] = useState();
    const [lines, setLines] = useState();
    const [polygons, setPolygons] = useState();

    const divisionId = division?.id ? toString(division?.id) : null
    const permitId = division.permit_id;

    const fetchMapPointers = async () => {
        try {
            let url = `/shared/permit/data/${permitId}/features.json`;
            if(IS_LAND_PERMIT(permitId)) {
                url = `/shared/land-permit/data/${permitId}/features.json`;
            }
            if(permitId === LOWERDESCHUTES_PERMIT_ID) {
                url = `/shared/lowerdeschutes/data/${permitId}/features.json`;
            }
            if(permitId === BOUNDARY_WATERS_ID) {
                url = `/shared/boundarywaters/data/${permitId}/features.json`;
            }
            if(permitId === GRAND_TETON_ID) {
                url = `/shared/grandteton/data/${permitId}/features.json`;
            }
            
            const response = await axios.get(url);
            setGeoData(response.data);
        }
        catch {
            // dont render map
        }
    }

    useEffect(() => {
        if(divisionId) {
            fetchMapPointers()
        }
    }, [divisionId])

    useEffect(() => {
        setPoints(filterFeatures(geoData?.points, divisionId));
        setLines(filterFeatures(geoData?.lines, divisionId));
        setPolygons(filterFeatures(geoData?.polygons, divisionId));
    }, [geoData, divisionId])

    if (!divisionId || (!points && !lines && !polygons)) {
        return null;
    }

    return (
        <div className="permit-division-map" id="division-map" style={MAP_STYLE}>
            <PermitMap
                fullscreenControl={false}
                geoJsonPoints={points}
                geoJsonLines={lines}
                geoJsonPolygons={polygons}
                mapContainer="division-map"
                zoom={17}
            />
        </div>
    );

};

type propTypes = {
    permit: PermitType,
    divisionId: string,
    hasMap?: boolean,
};

export default function DivisionDetail({ permit, divisionId, hasMap }: propTypes) {
    const division = permit?.divisions?.[divisionId];

    if (!division) {
        return <Spinner isCentered size={"xl"} />;
    }

    return (
        <Stack>
            <DivisionDescription division={division} />
            {hasMap && <DivisionMap division={division} />}
            <DivisionNeedToKnow division={division} />
        </Stack>
    );
}
